/* eslint-disable react/display-name */
import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@tapresearch/ui/components';
import {
  getCustomNivoBarItem,
  getPortalRenderedLegend,
} from '@tapresearch/ui/charts';

import { BarDatum, ComputedDatum, ResponsiveBar } from '@nivo/bar';

import { useBreakpoint } from '../../../../../shared/Hooks';
import { TapAnswer } from '../../../../api';
import { useColorScheme } from '../../../project/longitudinal-results';
import { getCustomBarLabel } from './CustomBarLabel';
import { CustomBarLegends } from './CustomBarLegends';
import { getCustomCrossTabBarLabel } from './CustomCrossTabBarLabel';
import { CustomCrossTabTooltip } from './CustomCrossTabTooltip';
import { CustomTick } from './CustomTick';
import { ImageChoiceLegends } from './ImageChoiceLegends';
import { getImageTick } from './ImageChoiceTick';

export interface StackedBarCharProps {
  data: BarDatum[];
  isCrossTab: boolean;
  isImageChoice?: boolean;
  tapAnswers?: TapAnswer[];
  qualificationLabels?: Record<string, string>;
  legendContainerId?: string;
  usePercentage: boolean;
}

export const StackedBarChart = ({
  data,
  tapAnswers,
  isCrossTab,
  usePercentage,
  isImageChoice = false,
  legendContainerId,
}: StackedBarCharProps) => {
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const { isMd } = useBreakpoint();

  const keys = isCrossTab
    ? tapAnswers.map((ta) => ta.attributes.id.toString())
    : data.map((answer) => answer.answer_id.toString());

  const tapAnswersById = useMemo(() => {
    return tapAnswers?.reduce<Record<string, TapAnswer['attributes']>>(
      (acc, answer) => {
        return {
          ...acc,
          [answer.attributes.id.toString()]: answer.attributes,
        };
      },
      {}
    );
  }, [tapAnswers]);

  const toggleSerie = useCallback(
    (serieId: string) => {
      const hiddenIdsSet = new Set(hiddenIds);
      if (hiddenIdsSet.has(serieId)) {
        hiddenIdsSet.delete(serieId);
      } else {
        hiddenIdsSet.add(serieId);
      }
      setHiddenIds([...hiddenIdsSet]);
    },
    [hiddenIds]
  );

  const BarLegends = useMemo(() => {
    return getPortalRenderedLegend(
      `#${legendContainerId}`,
      isImageChoice ? ImageChoiceLegends : CustomBarLegends,
      {
        data,
        hiddenSeries: hiddenIds,
        toggleSerie,
        answersById: tapAnswersById,
      },
      'bars'
    );
  }, [
    legendContainerId,
    data,
    hiddenIds,
    toggleSerie,
    tapAnswersById,
    isImageChoice,
  ]);

  const CustomBarLabel = useMemo(() => {
    return getCustomBarLabel({ usePercentage, answersById: tapAnswersById });
  }, [tapAnswersById, usePercentage]);

  const CustomCrossTabBarLabel = useMemo(() => {
    return getCustomCrossTabBarLabel({ usePercentage });
  }, [usePercentage]);

  const CustomCrossTabBarComponent = useMemo(() => {
    if (isCrossTab) return getCustomNivoBarItem(CustomCrossTabBarLabel);
  }, [isCrossTab, CustomCrossTabBarLabel]);

  const CustomBarComponent = useMemo(() => {
    return getCustomNivoBarItem(CustomBarLabel, {
      height: 32,
      showBarBackground: true,
    });
  }, [CustomBarLabel]);

  const LeftAxisTick = useMemo(() => {
    return getImageTick(tapAnswersById);
  }, [tapAnswersById]);

  const filteredData = useMemo(() => {
    if (isCrossTab) {
      return data.reduce<BarDatum[]>((acc, serieData) => {
        const newSerieData = { ...serieData };

        hiddenIds.forEach((hiddenId) => {
          delete newSerieData[hiddenId];
          delete newSerieData[`${hiddenId}_total`];
        });
        acc.push(newSerieData);
        return acc;
      }, []);
    }
    return data;
  }, [hiddenIds, data, isCrossTab]);

  const colors = useColorScheme();

  return (
    <Box
      w="100%"
      h="100%"
      overflowX={{ base: 'scroll', md: 'visible' }}
      sx={{
        svg: {
          overflow: 'visible',
        },
      }}
    >
      <Box
        w="100%"
        h="100%"
        {...(isCrossTab && {
          minW: '500px',
        })}
      >
        <ResponsiveBar
          {...{
            margin: {
              top: 30,
              right: isCrossTab ? 20 : 0,
              bottom: 30,
              left: isCrossTab ? (isMd ? 120 : 80) : 0,
            },
            indexBy: 'answer_id',
            data: filteredData,
            keys,
            colors,
            maxValue: 100,
            padding: 0.25,
            axisLeft: !!isCrossTab && {
              renderTick: CustomTick,
            },
            axisBottom: null,
            enableGridX: false,
            enableGridY: !!isCrossTab,
            layout: 'horizontal',

            ...(!isCrossTab
              ? {
                  label: (d: ComputedDatum<BarDatum>) => {
                    return `${tapAnswersById[d.id.toString()]?.option_text} - ${
                      d.value
                    }`;
                  },
                  axisLeft: isImageChoice
                    ? {
                        renderTick: LeftAxisTick,
                      }
                    : false,

                  barComponent: CustomBarComponent,
                  colors: ['#99DBD4'],
                  tooltipLabel: (props) => {
                    return tapAnswersById[props.id]?.option_text;
                  },
                }
              : {
                  tooltip: (props) => {
                    return (
                      <CustomCrossTabTooltip
                        barData={props}
                        answersById={tapAnswersById}
                      />
                    );
                  },
                  barComponent: CustomCrossTabBarComponent,
                  layers: [
                    'grid',
                    'axes',
                    'bars',
                    'markers',
                    BarLegends,
                    'annotations',
                  ],
                  legends: [
                    {
                      toggleSerie: true,
                      dataFrom: 'indexes',
                      anchor: 'left',
                      direction: 'column',
                      itemWidth: 1,
                      itemHeight: 1,
                    },
                  ],
                }),
          }}
        />
      </Box>
    </Box>
  );
};
